///
/// EarthBrowser
///
/// React hook for querying weather grid data locally.
/// Returns data in the same shape as the existing API responses
/// so the UI components require minimal changes.
///
/// Created by Matt Giger on 4/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { CurrentWeatherResponse, DailyForecastResponse, HourlyForecastResponse } from "@/types/WeatherEntry"
import { useEffect, useRef, useState } from "react"
import { computeHourlyForecast, type CurrentWeather, type DailyForecast as GridDailyForecast } from "./WeatherCompute"
import { getWeatherGridService } from "./WeatherGridService"

function degreesToCardinal(deg: number | null): string | null {
	if (deg === null) return null
	const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
	const idx = Math.round(deg / 22.5) % 16
	return dirs[idx]
}

function toCurrentWeatherResponse(lat: number, lon: number, cw: CurrentWeather): CurrentWeatherResponse {
	return {
		location: { lat, lon },
		current: {
			time: cw.time,
			source: "analysis",
			station: null,
			temperature: {
				value: cw.temp_c ?? 0,
				unit: "celsius",
				feels_like: cw.feels_like_c ?? cw.temp_c ?? 0,
			},
			humidity: cw.humidity_pct,
			dewpoint: cw.dewpoint_c,
			pressure: {
				value: cw.pressure_hpa,
				unit: "hPa",
				trend_3h: cw.pressure_trend_3h_hpa,
			},
			wind: {
				speed: cw.wind_speed_mps,
				gust: cw.wind_gust_mps,
				direction: cw.wind_dir_deg,
				cardinal: degreesToCardinal(cw.wind_dir_deg),
				unit: "m/s",
			},
			visibility: null,
			clouds: {
				cover: cw.cloud_cover_pct,
				ceiling: null,
			},
			weather: {
				code: cw.weather_code,
				description: cw.weather_desc,
				icon: cw.weather_code,
			},
			precipitation: {
				last_hour: cw.precip_rate_mmh,
			},
		},
	}
}

function toDailyForecastResponse(lat: number, lon: number, forecasts: GridDailyForecast[]): DailyForecastResponse {
	return {
		location: { lat, lon },
		generated: new Date().toISOString(),
		daily: forecasts.map(f => ({
			date: f.date,
			temperature: {
				min: f.temp_min_c ?? 0,
				max: f.temp_max_c ?? 0,
				morning: null,
				afternoon: null,
				evening: null,
				night: null,
			},
			humidity: {
				min: f.humidity_min_pct,
				max: f.humidity_max_pct,
			},
			wind: {
				speed: f.wind_speed_mps,
				gust: f.wind_gust_mps,
				direction: f.wind_dir_deg,
				cardinal: degreesToCardinal(f.wind_dir_deg),
			},
			precipitation: {
				total: f.precip_total_mm,
				probability: f.precip_probability,
			},
			cloud_cover: f.cloud_cover_pct,
			weather: f.weather,
		})),
	}
}

interface GridQueryResult<T> {
	data: T | undefined
	isLoading: boolean
	isError: boolean
	error?: unknown
}

export function useCurrentWeatherGrid(lat: number | null, lon: number | null): GridQueryResult<CurrentWeatherResponse> {
	const [data, setData] = useState<CurrentWeatherResponse | undefined>(undefined)
	const [isLoading, setIsLoading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState<unknown>(undefined)
	const abortRef = useRef(0)

	useEffect(() => {
		if (lat === null || lon === null) {
			setData(undefined)
			return
		}

		const requestId = ++abortRef.current
		setIsLoading(true)
		setIsError(false)

		const service = getWeatherGridService()
		service.getCurrentWeather(lat, lon)
			.then(result => {
				if (requestId !== abortRef.current) return
				if (result) {
					setData(toCurrentWeatherResponse(lat, lon, result))
				} else {
					setData(undefined)
				}
				setIsLoading(false)
			})
			.catch(err => {
				if (requestId !== abortRef.current) return
				setIsError(true)
				setError(err)
				setIsLoading(false)
			})
	}, [lat, lon])

	return { data, isLoading, isError, error }
}

function toHourlyForecastResponse(lat: number, lon: number, timesteps: ReturnType<typeof computeHourlyForecast>): HourlyForecastResponse {
	return {
		location: { lat, lon },
		generated: new Date().toISOString(),
		hourly: timesteps.map(h => ({
			time: h.time,
			temperature: h.temp_c ?? 0,
			humidity: h.humidity_pct,
			wind_speed: h.wind_speed_mps,
			wind_gust: h.wind_gust_mps,
			wind_direction: h.wind_dir_deg,
			wind_cardinal: degreesToCardinal(h.wind_dir_deg),
			// Grid precip is accumulation over the ~3h timestep
			precipitation_rate: h.precip_mm,
			precipitation_total: h.precip_mm,
			cloud_cover: h.cloud_cover_pct,
			pressure: h.pressure_hpa,
			weather: h.weather,
		})),
	}
}

export function useHourlyForecastGrid(lat: number | null, lon: number | null, hours: number = 24): GridQueryResult<HourlyForecastResponse> {
	const [data, setData] = useState<HourlyForecastResponse | undefined>(undefined)
	const [isLoading, setIsLoading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState<unknown>(undefined)
	const abortRef = useRef(0)

	useEffect(() => {
		if (lat === null || lon === null) {
			setData(undefined)
			return
		}

		const requestId = ++abortRef.current
		setIsLoading(true)
		setIsError(false)

		const service = getWeatherGridService()
		service.getHourlyForecast(lat, lon, hours)
			.then(result => {
				if (requestId !== abortRef.current) return
				setData(toHourlyForecastResponse(lat, lon, computeHourlyForecast(result)))
				setIsLoading(false)
			})
			.catch(err => {
				if (requestId !== abortRef.current) return
				setIsError(true)
				setError(err)
				setIsLoading(false)
			})
	}, [lat, lon, hours])

	return { data, isLoading, isError, error }
}

export function useDailyForecastGrid(lat: number | null, lon: number | null, days: number = 5): GridQueryResult<DailyForecastResponse> {
	const [data, setData] = useState<DailyForecastResponse | undefined>(undefined)
	const [isLoading, setIsLoading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState<unknown>(undefined)
	const abortRef = useRef(0)

	useEffect(() => {
		if (lat === null || lon === null) {
			setData(undefined)
			return
		}

		const requestId = ++abortRef.current
		setIsLoading(true)
		setIsError(false)

		const service = getWeatherGridService()
		service.getDailyForecast(lat, lon, days)
			.then(result => {
				if (requestId !== abortRef.current) return
				setData(toDailyForecastResponse(lat, lon, result))
				setIsLoading(false)
			})
			.catch(err => {
				if (requestId !== abortRef.current) return
				setIsError(true)
				setError(err)
				setIsLoading(false)
			})
	}, [lat, lon, days])

	return { data, isLoading, isError, error }
}
